import {
  PublicClientApplication, RedirectRequest,
} from '@azure/msal-browser';
import { authConfig } from 'config/authConfig';
import log from 'helpers/log';
import { compose, defaultToObj, trace } from 'helpers/utils';
import { LogLevel } from 'types/log';
import { createHmac } from 'crypto';

/*
simple hash challenge of the 'application' query string value
use a shared value and UTC date
*/
let pca: PublicClientApplication;

// DEV SHORTCUT: To test locally without needing to set up the login process, set the desktop application variable to true.
// This will bypass the login, and directly load into the sketchpad. It will have no access to the cloud services, however.
let desktopApplication = false;

// The desktop Application is launched through chromium.
// If we detect Report32.exe as a userAgent, we are in the desktop Application.
export const isDesktopApplication = (): boolean => {
  if (desktopApplication) return true;
  // the chromium control will have this userAgent
  if (!navigator.userAgent.match(/Report32.exe/)) {
    return false;
  }

  // get date in utc
  const utc = new Date();
  // a shared string
  const shared = Buffer.from('b831e171-3902-40a1-9dae-dd36fd8dd224');
  // hash from the query
  const appQuery = new URLSearchParams(window.location.search).get('application');
  if (!appQuery) {
    return false;
  }

  // not valid after time
  const nvaQuery = new URLSearchParams(window.location.search).get('nva');
  if (!nvaQuery) {
    return false;
  }

  // not valid after comes from the query
  const notValidAfter = parseInt(nvaQuery); // eslint-disable-line
  if (Number.isNaN(notValidAfter)) {
    return false;
  }

  // if now is after not valid after
  if (utc.getTime() > notValidAfter) {
    return false;
  }

  // the hour the hash was created
  const utcHashTime =
    utc.getUTCDate() +
    utc.getUTCFullYear() +
    utc.getUTCMonth() +
    utc.getUTCHours();

  // these need to match
  desktopApplication = appQuery === createHmac('sha256', shared).update(Buffer.from((utcHashTime + notValidAfter + navigator.userAgent)
    .toString())).digest('hex').toLowerCase();
  return desktopApplication;
};

// STP SSS-420
// The MobileApp attaches the ReactNativeWebView to the window.
// If the ReactNativeWebView is present, then this is the mobile app.
let mobileApplication = false;
export const isMobileApplication = (): boolean => {
  if (mobileApplication) return true;
  // set the boolean so we don't re-evaluate this over and over
  mobileApplication = window.ReactNativeWebView;
  return mobileApplication;
};

// The Workbench Application will provide a URL Parameter indicate the SketchMode.
// When this URL Parameter is present and matches "workbench", we are in the workbench.
// * In the future, perhaps we should check for the Iframe instead.
let workbenchApplication = false;
export const isWorkbenchApplication = (): boolean => {
  if (workbenchApplication) return true;

  let sketchModeId;
  const storedParams = sessionStorage.getItem('urlParams');
  if (storedParams) {
    const queryParams = new URLSearchParams(storedParams);
    sketchModeId = queryParams.get('SketchMode');
  }

  if (sketchModeId && sketchModeId === 'workbench') {
    workbenchApplication = true;
  }
  return workbenchApplication;
};

// getSketchPadMode will check which mode the application is in, and return an enum
// that can be used instead of calling the checking functions directly.
export const enum SketchpadModeType {
  Default = 'default',
  Desktop = 'desktop',
  Mobile = 'mobile',
  Workbench = 'workbench',
  Unchecked = '',
}
let sketchPadMode = SketchpadModeType.Unchecked;
export const getSketchPadMode = (): SketchpadModeType => {
  if (sketchPadMode !== SketchpadModeType.Unchecked) return sketchPadMode;

  // NOTE: All modes are exclusive. We can only be in a single mode at a time.
  if (isDesktopApplication()) {
    sketchPadMode = SketchpadModeType.Desktop;
  } else if (isMobileApplication()) {
    sketchPadMode = SketchpadModeType.Mobile;
  } else if (isWorkbenchApplication()) {
    sketchPadMode = SketchpadModeType.Workbench;
  } else {
    sketchPadMode = SketchpadModeType.Default;
  }

  sessionStorage.setItem('sketchMode', sketchPadMode.toString());
  return sketchPadMode;
};

// utility functions
export const configureAuth = (): void => {
  authConfig.msalConfig.auth.authority = setAuthority();
  pca = new PublicClientApplication({ ...authConfig.msalConfig });
};

const getSession = async (): any => {
  let session;
  try {
    const accounts = await pca.getAllAccounts();
    [session] = accounts;
  } catch (err) {
    if (authConfig.debugEnabled) {
      log(LogLevel.debug, 'getSession - unable to get current session', err);
    }
  }
  return session;
};

export const getUserIsValid = async (): Promise<boolean> => {
  const accounts = await pca.getAllAccounts();
  const valid = accounts.length > 0;

  if (authConfig.debugEnabled) {
    log(LogLevel.debug, 'getUserIsValid', valid);
  }

  return valid;
};

export const getAccessToken = async (): any => {
  pca.config.auth.authority = await setAuthority();
  const request = compose(
    (account: any) => ({
      scopes: [authConfig.eagleScope],
      account,
    }),
    ([acnt]: AccountInfo[]) => defaultToObj(acnt)
  )(pca.getAllAccounts());

  return pca.acquireTokenSilent(request).then((response) => response.accessToken)
    .catch(async (error) => trace(error))
    .catch(error => trace(error));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getUserInfo = async (): Promise<{ [id: string]: any } | undefined> => {
  const session = await getSession();
  const userInfo = session && session.getIdToken().decodePayload();
  if (authConfig.debugEnabled) {
    log(LogLevel.debug, 'getUserInfo', userInfo);
  }
  return userInfo;
};

export const getEmail = async (): Promise<string | undefined> => {
  const userInfo = await getUserInfo();
  return userInfo && userInfo.email;
};

export const setAuthority = async (): string => {
  const session = await getSession();
  if (session !== undefined) {
    if (Object.prototype.hasOwnProperty.call(session.idTokenClaims, 'AzureObjectID')) {
      return authConfig.b2cPolicies.authorities.signUpSignIn.authority;
    }
    return authConfig.b2cPolicies.authorities.b2esignin.authority;
  }
  return authConfig.b2cPolicies.authorities.signUpSignIn.authority;
};

export const login = async (policy: RedirectRequest | undefined): Promise<void> => {
  // These values will be overwritten and lost by the authentication redirect
  sessionStorage.setItem('redirectUrl', window.location.href);

  const referrer = document.referrer.toString();
  if (!referrer.includes('sketch')) sessionStorage.setItem('referringDomain', referrer);

  // Redirect to Login, which will redirect back with different parameters.
  await pca.handleRedirectPromise();
  await pca.loginRedirect(policy);
};

// This function cleans up session/local storage before Logout or Exit
export const beforeLogout = async (): Promise<void> => {
  await sessionStorage.removeItem('userCompany');
  await sessionStorage.removeItem('urlParams');
  await localStorage.clear();
};

export const doSketchExit = async (orderId:string): Promise<void> => {
  if (isWorkbenchApplication()) {
    // EDGE CASE: Return to Workbench
    await returnToWorkbench(beforeLogout, orderId);
  } else {
    // DEFAULT CASE: Logout
    await logout(beforeLogout);
  }
};

// Logout, redirecting to the login screen (STANDALONE ONLY)
export const logout = async (preLogout: () => void): Promise<void> => {
  pca.config.auth.authority = await setAuthority();

  try {
    await pca.handleRedirectPromise();
  } catch (err) {
    console.warn('handleRedirectPromise has hit an issue:', err);
  }

  if (preLogout) {
    await preLogout();
  }

  await pca.logoutRedirect();
};

// Return to the workbench order (WORKBENCH ONLY)
export const returnToWorkbench = async (preReturn: () => void, orderId: string): Promise<void> => {
  if (preReturn) {
    await preReturn();
  }

  const env = 'prod';
  const domain = `https://workbench.aci${env}.io`;
  const encodedOrderId = encodeURIComponent(orderId);

  window.location.href = `${domain}/reportbuilder/sketch/${encodedOrderId}`;
};
